import { AnyObjectSchema, InferType, ValidationError } from 'yup';

export type FormValues<Schema extends AnyObjectSchema> = InferType<Schema>;
interface ValidationResponse<Schema extends AnyObjectSchema> {
  error: boolean;
  errorMessages: {
    [K in keyof FormValues<Schema>]?: string;
  };
}

interface FormProps<Schema extends AnyObjectSchema> {
  schema: Schema;
  values: FormValues<Schema>;
}

export const validateForm = <Schema extends AnyObjectSchema>({
  schema,
  values,
}: FormProps<Schema>): ValidationResponse<Schema> => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return { error: false, errorMessages: {} };
  } catch (error) {
    if (error instanceof ValidationError) {
      const errors = error.inner.reduce<{
        [K in keyof FormValues<Schema>]?: string;
      }>((acc, err) => {
        if (err.path) {
          acc[err.path as keyof FormValues<Schema>] = err.message;
        }
        return acc;
      }, {});
      return { error: true, errorMessages: errors };
    }
    return { error: true, errorMessages: {} };
  }
};

type SchemaKeys<Schema extends AnyObjectSchema> = keyof InferType<Schema>;
export function extractKeys<Schema extends AnyObjectSchema>(
  schema: Schema
): Array<SchemaKeys<Schema>> {
  const shape = (schema as any).fields || (schema as any).shape;
  if (typeof shape !== 'object' || shape === null) {
    throw new Error(
      'The provided schema does not have a shape or fields method.'
    );
  }
  return Object.keys(shape) as Array<SchemaKeys<Schema>>;
}
